import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type FiledRefund, type RefundReasonOption } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';

interface Photo {
  id: string;
  preview: string;
  path: string | null;
  error: string | null;
}

/**
 * Reporting a problem with one pharmacy's delivery.
 *
 * ── WHY THE REASONS ARRIVE FROM THE API ─────────────────────────────────────
 *
 * Which reasons are still open is a money rule, enforced server-side (§4.5).
 * If this screen worked it out for itself, a buyer could fill in a whole form
 * for "It never arrived" and be refused at the end because they had already
 * given the rider their code. So the API sends every reason, marks which are
 * open, and says in plain words why the rest are not — and a closed reason is
 * shown with that explanation rather than silently removed, because a missing
 * option reads as the app hiding something.
 *
 * ── WHY SAFETY COMES FIRST ──────────────────────────────────────────────────
 *
 * "This may be fake" and "I had a reaction" are always available (§9.17) and
 * sit at the top. The person reaching for them may have already swallowed the
 * medicine, and they are told what to do about THAT before anything about
 * refunds.
 */
export function ReportProblemScreen() {
  const { reference, subOrder } = useParams<{ reference: string; subOrder: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<'reason' | 'details' | 'done'>('reason');
  const [reasonKey, setReasonKey] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [declared, setDeclared] = useState(false);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [result, setResult] = useState<FiledRefund | null>(null);

  // One key per attempt at a claim. Reused on a network retry, so a dropped
  // response is still one claim; replaced whenever the server has actually
  // answered or the buyer changes what they are claiming.
  const idempotencyKey = useRef(newIdempotencyKey());
  const fileInput = useRef<HTMLInputElement>(null);
  const photosRef = useRef<Photo[]>([]);

  // Kept in step outside render, so the unmount cleanup below can see the
  // latest photos without re-running on every change.
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Previews are object URLs, which the browser keeps alive until told not to.
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  const orderPath = `/orders/${reference ?? ''}`;

  const options = useQuery({
    queryKey: ['refund-options', subOrder],
    queryFn: ({ signal }) => api.refundOptions(subOrder!, signal),
    enabled: Boolean(subOrder),
  });

  const data = options.data;
  const reason = data?.reasons.find((option) => option.key === reasonKey) ?? null;

  const filing = useMutation({
    mutationFn: () =>
      api.fileRefundRequest(
        subOrder!,
        {
          reasonCode: reason!.key,
          description: description.trim() || undefined,
          media: photos.map((photo) => photo.path).filter((path): path is string => Boolean(path)),
          declaration: declared,
        },
        idempotencyKey.current,
      ),
    onSuccess: (filed) => {
      setResult(filed);
      setStep('done');
      void queryClient.invalidateQueries({ queryKey: ['order', reference] });
      void queryClient.invalidateQueries({ queryKey: ['refund-options', subOrder] });
    },
    onError: (error) => {
      // The server answered, so this key is spent. A dropped connection is not
      // an ApiError, and keeps its key for the retry.
      if (error instanceof ApiError) idempotencyKey.current = newIdempotencyKey();
    },
  });

  async function addFiles(files: FileList | null) {
    if (!files || !data) return;

    const room = data.max_photos - photos.length;
    const chosen = Array.from(files).slice(0, Math.max(0, room));

    for (const chosenFile of chosen) {
      const photo: Photo = {
        id: newIdempotencyKey(),
        preview: URL.createObjectURL(chosenFile),
        path: null,
        error: null,
      };

      setPhotos((current) => [...current, photo]);

      try {
        const uploaded = await api.uploadRefundEvidence(chosenFile);

        setPhotos((current) =>
          current.map((item) => (item.id === photo.id ? { ...item, path: uploaded.path } : item)),
        );
      } catch (error) {
        const message =
          error instanceof ApiError
            ? (error.fieldErrors.file?.[0] ?? error.message)
            : 'That photo did not upload. Check your connection and try again.';

        setPhotos((current) =>
          current.map((item) => (item.id === photo.id ? { ...item, error: message } : item)),
        );
      }
    }
  }

  function removePhoto(id: string) {
    setPhotos((current) => {
      const photo = current.find((item) => item.id === id);
      if (photo) URL.revokeObjectURL(photo.preview);

      return current.filter((item) => item.id !== id);
    });
  }

  function chooseReason(key: string) {
    setReasonKey(key);
    idempotencyKey.current = newIdempotencyKey();
    filing.reset();
  }

  const bar = <AppBar back title="Report a problem" />;

  if (options.isError) {
    return (
      <>
        {bar}
        <ErrorState
          title="We couldn't load this"
          body="Nothing has been sent. This is a connection problem."
          action={
            <Button full icon="refresh" onClick={() => options.refetch()}>
              Try again
            </Button>
          }
        />
      </>
    );
  }

  if (!data) {
    return (
      <>
        {bar}
        <div className="mx-auto w-full max-w-[560px] space-y-3 px-4 py-5">
          <Skeleton className="h-7 w-52" />
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} data-skeleton className="h-[68px] w-full" radius="card" />
          ))}
        </div>
      </>
    );
  }

  const fieldErrors = filing.error instanceof ApiError ? filing.error.fieldErrors : {};

  // ---------------------------------------------------------------------
  // Done.
  // ---------------------------------------------------------------------

  if (step === 'done' && result) {
    const copy = {
      paid: { eyebrow: 'Refunded', title: 'Your refund is on its way' },
      clinical_review: { eyebrow: 'Reported', title: 'A clinician will read this' },
      review: { eyebrow: 'Received', title: 'We have your request' },
    }[result.outcome];

    return (
      <>
        {bar}
        <div className="mx-auto w-full max-w-[560px] px-4 py-6">
          <Card className="!p-5 sm:!p-6">
            <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              {copy.eyebrow}
            </p>
            <h1 className="m-0 mt-2 font-display text-[23px] font-bold leading-[1.2] tracking-[-0.025em]">
              {copy.title}
            </h1>
            <p className="m-0 mt-2.5 text-[14px] leading-relaxed text-ink">{result.message}</p>

            <p className="m-0 mt-5 flex items-baseline justify-between gap-3 border-t border-line pt-3.5 text-[12px] text-muted">
              Your reference
              <b
                data-testid="claim-reference"
                className="font-mono text-[13px] tracking-[0.02em] text-ink"
              >
                {result.claim.reference}
              </b>
            </p>

            <Button full className="mt-5" onClick={() => navigate(orderPath, { replace: true })}>
              Back to your order
            </Button>
          </Card>
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // Nothing to report against.
  // ---------------------------------------------------------------------

  if (!data.reportable) {
    return (
      <>
        {bar}
        <div className="mx-auto w-full max-w-[560px] px-4 py-6">
          <Card className="!p-5">
            {data.open_claim ? (
              <>
                <h1 className="m-0 font-display text-[19px] font-bold tracking-[-0.02em]">
                  {data.open_claim.status_label}
                </h1>
                <p className="m-0 mt-1.5 text-[13px] leading-relaxed text-muted">
                  {data.open_claim.reason_label} · filed{' '}
                  {data.open_claim.filed_at
                    ? new Date(data.open_claim.filed_at).toLocaleDateString('en-NG')
                    : 'recently'}
                  . We will message you as soon as it is decided — there is nothing more you need to
                  do.
                </p>
                <p className="m-0 mt-3 text-[12px] text-muted">
                  Reference <b className="font-mono text-ink">{data.open_claim.reference}</b>
                </p>
              </>
            ) : (
              <p className="m-0 text-[13.5px] leading-relaxed text-ink">
                {data.not_reportable_because ?? 'There is nothing to report for this delivery.'}
              </p>
            )}

            <Link
              to={orderPath}
              className="mt-5 flex min-h-12 items-center justify-center rounded-btn border border-line bg-white px-5 text-[14px] font-bold text-ink no-underline"
            >
              Back to your order
            </Link>
          </Card>
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // Step 1 — what went wrong.
  // ---------------------------------------------------------------------

  if (step === 'reason' || !reason) {
    const open = data.reasons.filter((option) => option.available);
    const closed = data.reasons.filter((option) => !option.available);

    return (
      <>
        {bar}
        <div className="mx-auto w-full max-w-[560px] px-4 py-5">
          <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">
            What went wrong?
          </h1>
          <p className="m-0 mt-1 text-[13px] text-muted">
            {data.sub_order.pharmacy} · {data.sub_order.goods_total_display}
          </p>

          {/* Grouped in words rather than colour-coded. A safety report and a
              complaint about the packing are different kinds of thing, and a
              heading says which is which; a coloured stripe down the edge of a
              card only decorates it. */}
          <fieldset className="m-0 mt-5 space-y-5 border-0 p-0">
            <legend className="sr-only">Choose a reason</legend>

            <ReasonGroup
              label="If the medicine may be unsafe"
              options={open.filter((option) => option.is_safety)}
              selected={reasonKey}
              onChoose={chooseReason}
            />
            <ReasonGroup
              label="Something wrong with the order"
              options={open.filter((option) => !option.is_safety)}
              selected={reasonKey}
              onChoose={chooseReason}
            />
          </fieldset>

          <Button
            full
            className="mt-4"
            disabled={!reasonKey}
            onClick={() => {
              setStep('details');
              window.scrollTo(0, 0);
            }}
          >
            Continue
          </Button>

          <ReasonGroup
            className="mt-7"
            label="Not available for this delivery"
            options={closed}
            selected={null}
            onChoose={() => {}}
          />
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------------
  // Step 2 — evidence, account, declaration.
  // ---------------------------------------------------------------------

  const uploaded = photos.filter((photo) => photo.path).length;
  const uploading = photos.some((photo) => !photo.path && !photo.error);
  const written = description.trim().length;

  const canSend =
    declared &&
    !uploading &&
    uploaded >= reason.minimum_photos &&
    (!reason.requires_description || written >= Math.max(1, reason.minimum_description)) &&
    !filing.isPending;

  return (
    <>
      {bar}
      <div className="mx-auto w-full max-w-[560px] px-4 py-5">
        <button
          type="button"
          onClick={() => setStep('reason')}
          className="-mx-1 mb-2 inline-flex min-h-11 items-center gap-1.5 rounded-btn px-1 text-[12.5px] font-medium text-blue"
        >
          <Icon name="back" size={14} strokeWidth={2.4} />
          Change reason
        </button>

        <h1 className="m-0 font-display text-[22px] font-bold tracking-[-0.02em]">{reason.label}</h1>
        <p className="m-0 mt-1 text-[13px] text-muted">{reason.hint}</p>

        {/* Before any word about refunds: what to do right now.

            Set in type rather than dropped into a tinted box. This is the most
            important sentence on the screen and a coloured panel makes it look
            like a cookie notice — the thing every reader has trained
            themselves to skip. */}
        {reason.key === 'adverse_reaction' ? (
          <SafetyNote label="Before anything else">
            If you feel unwell now, stop taking it and speak to a pharmacist or doctor straight
            away. Do not wait to hear from us.
          </SafetyNote>
        ) : null}
        {reason.key === 'not_genuine' ? (
          <SafetyNote label="Keep the pack">
            Do not take any more of it. Keep the pack and the bag — we may ask to collect them.
          </SafetyNote>
        ) : null}

        <form
          className="mt-5 space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSend) filing.mutate();
          }}
        >
          <section>
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="m-0 text-[14px] font-bold text-ink">Photos</h2>
              {reason.minimum_photos > 0 ? (
                <span
                  className={cn(
                    'text-[12px] tabular-nums',
                    uploaded >= reason.minimum_photos ? 'text-verified' : 'text-muted',
                  )}
                >
                  {Math.min(uploaded, reason.minimum_photos)} of {reason.minimum_photos}
                </span>
              ) : null}
            </div>
            <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-muted">
              {reason.minimum_photos > 0
                ? `At least ${reason.minimum_photos} — the pack, the label and the batch number, so we can act without asking you twice.`
                : 'Optional — add any that help explain what happened.'}
            </p>

            {photos.length > 0 ? (
              <ul className="m-0 mt-3 grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-4">
                {photos.map((photo) => (
                  <li
                    key={photo.id}
                    data-testid="evidence-thumb"
                    className="relative aspect-square overflow-hidden rounded-[10px] border border-line bg-paper"
                  >
                    <img src={photo.preview} alt="" className="h-full w-full object-cover" />
                    {!photo.path && !photo.error ? (
                      <span className="absolute inset-0 grid place-items-center bg-white/70 text-[11px] font-semibold text-muted">
                        Uploading…
                      </span>
                    ) : null}
                    {photo.error ? (
                      <span className="absolute inset-x-0 bottom-0 bg-danger px-1.5 py-1 text-[10px] leading-tight text-white">
                        {photo.error}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      aria-label="Remove photo"
                      onClick={() => removePhoto(photo.id)}
                      className="absolute right-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-ink/60 text-[15px] leading-none text-white backdrop-blur-sm transition-colors hover:bg-ink"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              data-testid="evidence-input"
              onChange={(event) => {
                void addFiles(event.target.files);
                event.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="grey"
              className="mt-3"
              disabled={photos.length >= data.max_photos}
              onClick={() => fileInput.current?.click()}
            >
              {photos.length === 0 ? 'Add photos' : 'Add another photo'}
            </Button>
            {fieldErrors.media?.[0] ? (
              <p role="alert" className="m-0 mt-2 text-[12px] font-medium text-danger">
                {fieldErrors.media[0]}
              </p>
            ) : null}
          </section>

          <section>
            <label htmlFor="refund-description" className="block text-[14px] font-bold text-ink">
              {reason.requires_description ? 'What happened' : 'Anything else we should know?'}
              {!reason.requires_description ? (
                <span className="font-normal text-faint"> (optional)</span>
              ) : null}
            </label>
            {reason.requires_description ? (
              <p className="m-0 mt-0.5 text-[12.5px] leading-relaxed text-muted">
                What you took, when it started, and what you felt. A clinician reads this.
              </p>
            ) : null}
            <textarea
              id="refund-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={5}
              maxLength={2000}
              className="mt-2 block w-full resize-y rounded-btn border border-line bg-white px-3 py-2.5 text-[14px] leading-relaxed text-ink outline-none focus:border-blue"
            />
            {reason.requires_description && reason.minimum_description > 0 ? (
              <p
                className={cn(
                  'm-0 mt-1 text-right text-[11.5px] tabular-nums',
                  written >= reason.minimum_description ? 'text-verified' : 'text-faint',
                )}
              >
                {written >= reason.minimum_description
                  ? 'That is enough to go on'
                  : `${reason.minimum_description - written} more characters`}
              </p>
            ) : null}
            {fieldErrors.description?.[0] ? (
              <p role="alert" className="m-0 mt-1 text-[12px] font-medium text-danger">
                {fieldErrors.description[0]}
              </p>
            ) : null}
          </section>

          <label
            className={cn(
              'relative flex cursor-pointer items-start gap-3 rounded-card border bg-white p-4 transition-colors',
              declared ? 'border-blue' : 'border-line hover:border-faint',
            )}
          >
            <input
              type="checkbox"
              checked={declared}
              onChange={(event) => setDeclared(event.target.checked)}
              className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
            <span
              aria-hidden
              className={cn(
                'mt-[1px] grid h-[19px] w-[19px] shrink-0 place-items-center rounded-[6px] border transition-colors',
                declared ? 'border-blue bg-blue text-white' : 'border-line bg-white',
                'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue',
              )}
            >
              {declared ? (
                <svg
                  viewBox="0 0 12 10"
                  aria-hidden
                  className="h-[9px] w-[11px] fill-none stroke-current"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 5.2 4.3 8.5 11 1.6" />
                </svg>
              ) : null}
            </span>
            <span className="text-[13.5px] leading-relaxed text-ink">
              What I have told you is true, and any photos are of the medicine I received.
            </span>
          </label>
          {fieldErrors.declaration?.[0] ? (
            <p role="alert" className="m-0 -mt-3 text-[12px] font-medium text-danger">
              {fieldErrors.declaration[0]}
            </p>
          ) : null}

          {fieldErrors.reason_code?.[0] ? (
            <p role="alert" className="m-0 text-[12.5px] font-medium leading-relaxed text-danger">
              {fieldErrors.reason_code[0]}
            </p>
          ) : null}
          {filing.error && !(filing.error instanceof ApiError && filing.error.isValidation) ? (
            <p role="alert" className="m-0 text-[12.5px] font-medium text-danger">
              {filing.error instanceof ApiError
                ? filing.error.message
                : 'We could not send that. Check your connection and try again — it will not be sent twice.'}
            </p>
          ) : null}

          <Button full type="submit" loading={filing.isPending} disabled={!canSend}>
            Send report
          </Button>
        </form>
      </div>
    </>
  );
}

/**
 * One reason, as a choice.
 *
 * A closed reason is rendered, disabled, with its explanation — never simply
 * left out. An option that silently vanishes reads as the app hiding it.
 */
function ReasonGroup({
  label,
  options,
  selected,
  onChoose,
  className,
}: {
  label: string;
  options: RefundReasonOption[];
  selected: string | null;
  onChoose: (key: string) => void;
  className?: string;
}) {
  if (options.length === 0) {
    return null;
  }

  return (
    <section className={className}>
      <h2 className="m-0 mb-2 px-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        {label}
      </h2>

      {/* One bordered block with hairlines between the rows, rather than a
          stack of floating cards. It reads as a list of choices, which is what
          it is. */}
      <div className="overflow-hidden rounded-card border border-line bg-white">
        {options.map((option) => (
          <ReasonChoice
            key={option.key}
            option={option}
            checked={selected === option.key}
            onChoose={() => onChoose(option.key)}
          />
        ))}
      </div>
    </section>
  );
}

function ReasonChoice({
  option,
  checked,
  onChoose,
}: {
  option: RefundReasonOption;
  checked: boolean;
  onChoose: () => void;
}) {
  const disabled = !option.available;

  return (
    <label
      className={cn(
        'relative flex items-start gap-3.5 border-b border-line-2 px-4 py-3.5 transition-colors last:border-b-0',
        disabled && 'cursor-not-allowed',
        !disabled && 'cursor-pointer',
        checked && 'bg-blue-tint-2',
        !checked && !disabled && 'hover:bg-paper',
      )}
    >
      <input
        type="radio"
        name="refund-reason"
        value={option.key}
        checked={checked}
        disabled={disabled}
        onChange={onChoose}
        // Transparent and full-size rather than visually hidden: a clipped
        // input has no box, which makes the row unreachable to a keyboard
        // user's pointer and invisible to anything driving the page.
        className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />

      {/* Drawn, not the browser's. A native radio is a different size and
          colour in every browser, and this one has to read as chosen at a
          glance on a phone held at arm's length. */}
      <span
        aria-hidden
        className={cn(
          'mt-[3px] h-[18px] w-[18px] shrink-0 rounded-full border bg-white transition-all',
          checked ? 'border-[5.5px] border-blue' : 'border-line',
          disabled && 'border-line-2 bg-paper',
          'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue',
        )}
      />

      <span className="min-w-0">
        <span
          className={cn(
            'block text-[14.5px] font-semibold tracking-[-0.01em]',
            disabled ? 'text-faint' : 'text-ink',
          )}
        >
          {option.label}
        </span>
        <span
          className={cn(
            'mt-0.5 block text-[12.5px] leading-relaxed',
            disabled ? 'text-faint' : 'text-muted',
          )}
        >
          {disabled ? option.why_not : option.hint}
        </span>
      </span>
    </label>
  );
}

/**
 * The one sentence on the screen that is not about money.
 *
 * Deliberately unboxed and uncoloured. A tinted panel with a warning icon is
 * the shape of an advert, and it is skipped the way adverts are skipped — so
 * this is set as editorial instead: a quiet label, a rule, and the sentence.
 */
function SafetyNote({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-5 border-t border-line pt-4">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">{label}</p>
      <p className="m-0 mt-1.5 max-w-[46ch] text-[14.5px] font-medium leading-[1.55] text-ink">
        {children}
      </p>
    </div>
  );
}

/** Works outside a secure context too, where `crypto.randomUUID` is missing. */
function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `rfd-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
